import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, Trash2 } from "lucide-react";
import { Button, Modal } from "../../../components";
import {
  PLATFORM_COMPANY_DOMAINS_URL,
  PLATFORM_VERIFY_DOMAIN_URL,
  PLATFORM_DELETE_DOMAIN_URL,
} from "../../../features/UrlHelper";

/**
 * Vlastne domeny prevadzky (palermo.sk). Nova domena je NEOVERENA -- CORS
 * ju nepusti a prevadzka sa podla nej nerozpozna, kym ju tu SUPERADMIN
 * neoveri. Pred overenim musi domena naozaj smerovat na nas frontend
 * (DNS + IIS binding + Turnstile a Google Maps povolene hostnames).
 */
function CompanyDomainsModal({ company, onClose }) {
  const [domeny, setDomeny] = useState([]);
  const [nova, setNova] = useState("");
  const [primarna, setPrimarna] = useState(false);
  const [chyba, setChyba] = useState("");
  const [pracuje, setPracuje] = useState(false);

  const nacitaj = useCallback(async () => {
    try {
      const { data } = await axios.get(PLATFORM_COMPANY_DOMAINS_URL(company.id));
      setDomeny(Array.isArray(data) ? data : []);
    } catch (err) {
      setChyba(err?.response?.data?.message || "Domény sa nepodarilo načítať.");
    }
  }, [company.id]);

  useEffect(() => {
    nacitaj();
  }, [nacitaj]);

  const akcia = async (fn) => {
    setPracuje(true);
    setChyba("");
    try {
      await fn();
      await nacitaj();
    } catch (err) {
      setChyba(err?.response?.data?.message || "Akcia zlyhala.");
    } finally {
      setPracuje(false);
    }
  };

  const pridaj = (e) => {
    e.preventDefault();
    akcia(async () => {
      await axios.post(PLATFORM_COMPANY_DOMAINS_URL(company.id), { domain: nova, is_primary: primarna });
      setNova("");
      setPrimarna(false);
    });
  };

  return (
    <Modal isOpen onClose={onClose} title={`Domény – ${company.name}`}>
      <div className="admin__form">
        {domeny.length === 0 ? (
          <p>Prevádzka nemá vlastnú doménu — beží na rozvozy.sk/{company.slug}.</p>
        ) : (
          <ul className="platform__list">
            {domeny.map((d) => (
              <li key={d.id} className="platform__list-item">
                <span>
                  <strong>{d.domain}</strong>
                  {d.is_primary && " · primárna"}
                  {" · "}
                  {d.verified_at ? (
                    <span className="platform__ok">overená</span>
                  ) : (
                    <span className="platform__expired">neoverená (CORS ju nepustí)</span>
                  )}
                </span>
                <span className="admin__table-actions">
                  {!d.verified_at && (
                    <button
                      className="admin__icon-btn"
                      title="Overiť"
                      disabled={pracuje}
                      onClick={() => akcia(() => axios.post(PLATFORM_VERIFY_DOMAIN_URL(d.id)))}
                    >
                      <BadgeCheck size={16} />
                    </button>
                  )}
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    title="Odobrať"
                    disabled={pracuje}
                    onClick={() => akcia(() => axios.delete(PLATFORM_DELETE_DOMAIN_URL(d.id)))}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={pridaj} className="platform__inline-form">
          <input
            className="admin__form-input"
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            placeholder="palermo.sk"
            required
          />
          <label className="platform__checkbox">
            <input type="checkbox" checked={primarna} onChange={(e) => setPrimarna(e.target.checked)} /> primárna
          </label>
          <Button variant="primary" type="submit" disabled={pracuje}>
            Pridať
          </Button>
        </form>

        {chyba && <p className="platform__error" role="alert">{chyba}</p>}

        <div className="admin__form-actions">
          <Button variant="ghost" onClick={onClose}>
            Zavrieť
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default CompanyDomainsModal;
